<template>
  <div class="admin-user-invitations px-2 px-md-4 py-2 py-md-4">
    <ConveyTable
      showSearch
      showFilter
      :showCreate="$permission('create:user')"
      tableClassName="admin-invitation-table"
      :items="invitations"
      :fields="showActionsColumn ? fields : fieldsNoActions"
      :placeholders="placeholders"
      :filterFields="filterFields"
      :filterOptions="filterOptions"
      :defaultFilters="defaultFilters"
      :enableClear="false"
      :enableClearAll="false"
      :perPageOptions="perPageOptions"
      :perPage="perPage"
      :currentPage="currentPage"
      :totalRows="totalRows"
      :isLoading="isFetchingInvitations"
      @onFetch="(options) => fetchInvitations(options)"
      @onCreate="addUser"
    >
      <template #create-icon>
        <b-icon icon="person-plus"></b-icon>
      </template>
      <template #create-label>
        Add User
      </template>
      <template #cell(fullName)="data">
        <router-link :to="`/admin/invitations/${data.item.invite_id}`">
          {{ data.value }}
        </router-link>
      </template>
      <template #cell(roles)="data">
        <span
          v-for="role in data.value"
          :key="`${data.item.invite_id}_${role}`"
          :class="`${getRoleType(role)} badge font-weight-medium mr-2 text-capitalize`"
        >
          {{ getRoleLabel(role) }}
        </span>
      </template>
      <template #cell(actions)="data">
        <div
          v-if="showActionsColumn"
          class="d-flex flex-nowrap justify-content-center align-items-center gap-2"
        >
          <b-button
            class="p-0"
            style="background-color:transparent;border:none;"
            @click="revokeInvitation(data.item)"
          >
            <p class="h4 m-0">
              <b-icon-trash title="Revoke invitation" variant="danger" />
            </p>
          </b-button>
          <b-button
            class="p-0"
            style="background-color:transparent;border:none;"
            @click="copyLink(data.item)"
          >
            <p class="h4 m-0">
              <b-icon-link45deg title="Copy invitation link" variant="info" />
            </p>
          </b-button>
          <b-button
            class="p-0"
            style="background-color:transparent;border:none;"
            @click="resendInvitation(data.item)"
          >
            <p class="h4 m-0">
              <b-icon-cursor title="Resend invitation email" variant="info" />
            </p>
          </b-button>
        </div>
      </template>
    </ConveyTable>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import vSelect from "vue-select";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import moment from "moment"
import ConveyTable from "@/components/ConveyTable";

export default {
  name: "AdminUserInvitations",
  mixins: [formatter, errorHandler],
  components: { vSelect, ConveyTable },
  data() {
    return {
      invitations: [],
      availableRoles: [],
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      totalRows: 0,
      placeholders: {
        search: "Search invitation by email..."
      },
      fields: [
        { key: "fullName", label: "Name", sortable: false },
        { key: "email", label: "Email", sortable: false },
        { key: "roles", label: "Role", sortable: false },
        { key: "formattedStatus", label: "Status", sortable: false },
        { key: "actions", label: "Actions", sortable: false, stickyColumn: true, class: "right-sticky-column actions-column" },
      ],
      fieldsNoActions: [
        { key: "fullName", label: "Name", sortable: false },
        { key: "email", label: "Email", sortable: false },
        { key: "roles", label: "Role", sortable: false },
        { key: "formattedStatus", label: "Status", sortable: false },
        { key: "fulfilledDate", label: "Fulfilled Date", sortable: false },
      ],
      defaultFilters: {
        status: {
          label: "Pending",
          text: "Pending",
          value: "pending",
        }
      },
      filterFields: [{ name: 'status', label: 'Status' }],
      filterOptions: {
        status: [
          {
            label: "Pending",
            text: "Pending",
            value: "pending",
          },
          {
            label: "Fulfilled",
            text: "Fulfilled",
            value: "fulfilled",
          },
        ],
      },
      isFetchingInvitations: false,
      resendingInviteIds: [],
      search: "",
      selectedFilters: [{key: "status", value: "pending"}],
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("agency", ["currentAgency"]),
    selectedStatus() {
      const filter = this.selectedFilters.find(i => i.key === "status");
      return filter;
    },
    showActionsColumn() {
      return !this.selectedStatus || this.selectedStatus.value === "pending";
    }
  },
  created() {
    try {
      this.availableRoles = this.currentAgency.roles.map(r => ({ ...r, text: r.role, value: r.role_id }));
    } catch (err) { }
  },
  methods: {
    getRoleType(roleId) {
      const role = this.availableRoles.find(item => item.value === roleId);
      if (role) {
        const roleText = role.text.toLowerCase();
        if (roleText.includes('admin')) {
          return 'admin';
        } else if (roleText.includes('operator')) {
          return 'operator';
        } else {
          return 'other';
        }
      } else {
        return 'unknown'
      }
    },
    getRoleLabel(roleId) {
      const role = this.availableRoles.find(item => item.value === roleId);
      if (role) {
        return role.text;
      } else {
        return 'Unknown'
      }
    },
    copyLink(invitation) {
      const { token } = invitation;
      if (token) {
        const tokenUri = `${this.$basescheme}://${this.$baseuri}/t/${token}`;
        navigator.clipboard.writeText(tokenUri).then(() => {
          this.$toastr("success", "Link copied!");
        });
      } else {
        this.$toastr("error", "The invitation link doesn't exist!");
      }
    },
    async resendInvitation(invitation) {
      if (confirm("Resend invitation email to this user?") !== true)
        return;

      const { invite_id } = invitation;

      if (this.resendingInviteIds.includes(invite_id)) {
        return;
      } else {
        this.resendingInviteIds.push(invite_id);
      }

      try {
        await this.$ConveyApi.invitationsApi.resendInvitation({ inviteId: invite_id });
        this.$toastr("success", "Email invitation has been resent.");
      } catch (err) {
        console.log(err);
        this.$toastr("error", "An error occurred when attempting to resend the email invitation");
      }

      if (this.resendingInviteIds.includes(invite_id)) {
        const index = this.resendingInviteIds.findIndex(i => i == invite_id);
        this.resendingInviteIds.splice(index, 1);
      }
    },
    async revokeInvitation(invitation) {
      if (confirm(`Revoke invitation for ${invitation.email}`) !== true) return;

      const { invite_id } = invitation;

      try {
        await this.$ConveyApi.invitationsApi.revokeInvitation({ inviteId: invite_id });
        this.fetchInvitations({
          perPage: this.perPage,
          currentPage: 1,
          searchKey: this.search,
          filter: this.selectedFilters,
        });
        this.$toastr("success", `Invitation for ${invitation.email} is revoked`);
      } catch (err) {
        console.log(err);
        this.$toastr(
          "error",
          "An error occurred when attempting to revoke the invitation"
        );
      }
    },
    abortFetchOperation() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchInvitations(fetchOptions) {
      try {
        this.abortFetchOperation();
        this.fetchAbortController = new AbortController();
        const {
          perPage,
          currentPage,
          filter,
          searchKey,
        } = fetchOptions;

        this.perPage = perPage;
        this.currentPage = currentPage;
        this.selectedFilters = [...filter];
        this.search = searchKey;
        this.isFetchingInvitations = true;

        const offset = (currentPage - 1) * perPage;
        const statusFilter = filter.find(i => i.key === "status");
        const statusAppendix = statusFilter && statusFilter.value == 'pending' ? `Pending` : '';
        const params = {
          offset,
          limit: perPage,
          fulfilled: statusFilter && statusFilter.value == 'pending' ? false : true,
        };
        if (searchKey)
          params['search'] = searchKey;

        const resp = await this.$ConveyApi.invitationsApi.getInvitations({
          params,
          isCurrentAgency: true,
          signal: this.fetchAbortController.signal,
        });
        const { data, metadata } = resp.data;
        this.invitations = data.map((item) => {
          const { roles, first_name, last_name, status, fulfilled_at } = item;
          const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1);
          return {
            ...item,
            fullName: `${first_name} ${last_name}`,
            roles: roles ? roles.split(',') : [],
            formattedStatus: status
              ? (statusAppendix ? `${statusAppendix} - ${capitalizedStatus}` : capitalizedStatus)
              : statusAppendix,
            fulfilledDate: fulfilled_at ? this.formatDate(moment(fulfilled_at), "MMM DD, YYYY h:mm a") : "",
          };
        });
        this.totalRows = metadata ? metadata.totalRows || 0 : 0;
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminUserInvitations:fetchInvitations] ERROR: ", err);
        }
      } finally {
        this.isFetchingInvitations = false;
      }
    },
    addUser() {
      this.$router.push({ name: "Admin Create User" }).catch(() => {});
    }
  },
  beforeDestroy() {
    this.abortFetchOperation();
  }
};
</script>

<style lang="scss">
.admin-user-invitations {
  width: 100%;
  height: 100%;

  .admin-invitation-table {
    span.badge.admin {
      background-color: #f3ced6 !important;
      color: #ef5350 !important;
    }

    span.badge.operator {
      background-color: #b7f1e0 !important;
      color: #0acc95 !important;
    }

    span.badge.other {
      background-color: #cbe2ff !important;
      color: #186dde !important;
    }

    span.badge.unknown {
      background-color: #cbe2ff !important;
      color: #186dde !important;
    }
  }
}
</style>